import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { can, getDriverScope } from '../permissions';
import {
  CheckCircle, Percent, TrendingUp, TrendingDown, Minus, MapPin, XCircle, Users, AlertTriangle, Clock, Building2,
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const C = { blue:'#0284c7', green:'#16a34a', red:'#dc2626', amber:'#f59e0b', purple:'#7c3aed', orange:'#ea580c', slate:'#64748b' };
const money = n => 'AED ' + Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const num = n => Number(n || 0).toLocaleString();
const mins = n => { const v = Math.round(Number(n || 0)); if (!v) return '—'; const h = Math.floor(v/60), m = v%60; return h ? `${h}h ${m}m` : `${m}m`; };
const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shortDate = d => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric' });
const niceDate = d => new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
const card = { background:'#fff', borderRadius:12, boxShadow:'0 1px 3px rgba(0,0,0,0.08)' };
const th = { padding:'10px 14px', textAlign:'left', color:'#64748b', fontWeight:500, fontSize:12 };
const td = { padding:'11px 14px', color:'#475569', fontSize:13 };

const DRIVER_TYPE_OPTIONS = [
  ['', 'All Types'],
  ['delivery', 'Delivery Driver'],
  ['presalesman', 'Presalesman'],
  ['van_salesman', 'Van Salesman'],
];

const PRESETS = [
  { key:'week', label:'This week', range: () => {
      const to = new Date(); const from = new Date(); from.setDate(from.getDate() - 6);
      return { from: iso(from), to: iso(to) };
    } },
  { key:'lastweek', label:'Last week', range: () => {
      const to = new Date(); to.setDate(to.getDate() - 7);
      const from = new Date(); from.setDate(from.getDate() - 13);
      return { from: iso(from), to: iso(to) };
    } },
  { key:'month', label:'This month', range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: iso(from), to: iso(now) };
    } },
  { key:'lastmonth', label:'Last month', range: () => {
      const now = new Date();
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: iso(from), to: iso(to) };
    } },
];

const KpiCard = ({ icon: Icon, label, value, color, current, previous, invert = false }) => {
  const cur = Number(current || 0), prev = Number(previous || 0);
  const diff = prev > 0 ? Math.round(((cur - prev) / prev) * 100) : (cur > 0 ? 100 : 0);
  const good = invert ? diff < 0 : diff > 0;
  const TrendIcon = diff === 0 ? Minus : diff > 0 ? TrendingUp : TrendingDown;
  const tint = diff === 0 ? C.slate : good ? C.green : C.red;
  return (
    <div style={{ ...card, padding:18, display:'flex', alignItems:'flex-start', gap:14, flex:1, minWidth:190 }}>
      <div style={{ background:color+'20', borderRadius:10, padding:10, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon size={20} color={color} />
      </div>
      <div style={{ minWidth:0 }}>
        <div style={{ fontSize:12, color:'#64748b', marginBottom:4 }}>{label}</div>
        <div style={{ fontSize:22, fontWeight:700, color:'#0f172a', lineHeight:1.15 }}>{value}</div>
        <div style={{ display:'flex', alignItems:'center', gap:4, marginTop:6, fontSize:11, color:tint, fontWeight:600 }}>
          <TrendIcon size={12} /> {diff === 0 ? 'No change' : `${Math.abs(diff)}% vs previous period`}
        </div>
      </div>
    </div>
  );
};

const Panel = ({ title, note, icon: Icon, children }) => (
  <div style={{ ...card, overflow:'hidden', marginBottom:16 }}>
    <div style={{ padding:'14px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', alignItems:'center', gap:8 }}>
      {Icon && <Icon size={15} color="#64748b" />}
      <div>
        <h2 style={{ fontSize:14, fontWeight:600, color:'#1e293b', margin:0 }}>{title}</h2>
        {note && <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>{note}</div>}
      </div>
    </div>
    <div style={{ padding:16 }}>{children}</div>
  </div>
);

const Empty = ({ text }) => <div style={{ padding:24, textAlign:'center', color:'#94a3b8', fontSize:13 }}>{text}</div>;

const MiniRank = ({ items, primary, secondary, valueLabel }) => (
  items.length ? (
    <div>
      {items.slice(0, 5).map((item, i) => (
        <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderTop: i ? '1px solid #f1f5f9' : 'none' }}>
          <div style={{ width:20, fontSize:11, color:'#94a3b8', fontWeight:700 }}>{i + 1}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:13, fontWeight:500, color:'#1e293b', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{primary(item)}</div>
            {secondary && <div style={{ fontSize:11, color:'#94a3b8' }}>{secondary(item)}</div>}
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:'#dc2626' }}>{valueLabel(item)}</div>
        </div>
      ))}
    </div>
  ) : <Empty text="None in this period" />
);

const driverScope = getDriverScope();
const visibleTypeOptions = driverScope
  ? DRIVER_TYPE_OPTIONS.filter(([val]) => val === '' || driverScope.includes(val))
  : DRIVER_TYPE_OPTIONS;

export default function ManagementReport() {
  const [presetKey, setPresetKey] = useState('week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const presetRange = useMemo(() => PRESETS.find(p => p.key === presetKey)?.range() || { from: '', to: '' }, [presetKey]);
  const from = presetKey === 'custom' ? customFrom : presetRange.from;
  const to = presetKey === 'custom' ? customTo : presetRange.to;
  useEffect(() => {
    if (presetKey !== 'custom') { setCustomFrom(presetRange.from); setCustomTo(presetRange.to); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey]);

  const [selType, setSelType] = useState('');
  const [trends, setTrends] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [problems, setProblems] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [routePerf, setRoutePerf] = useState(null);
  const [finGroup, setFinGroup] = useState('day');
  const [finSummary, setFinSummary] = useState(null);
  const [trendGroup, setTrendGroup] = useState('week');
  const [driverTrend, setDriverTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!from || !to) return;
    setLoading(true);
    const q = `?from=${from}&to=${to}${selType ? `&type=${selType}` : ''}`;
    Promise.all([
      api.get('/analytics/trends' + q),
      api.get('/analytics/driver-performance' + q),
      api.get('/analytics/problems' + q),
      api.get('/analytics/customer-breakdown' + q),
      api.get('/analytics/route-performance' + q),
    ]).then(([t, d, p, c, rp]) => {
      setTrends(t.data);
      setDrivers(d.data.drivers || []);
      setProblems(p.data);
      setCustomers(c.data.customers || []);
      setRoutePerf(rp.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [from, to, selType]);

  useEffect(() => {
    if (!from || !to) return;
    api.get(`/analytics/financial-summary?from=${from}&to=${to}&group=${finGroup}${selType ? `&type=${selType}` : ''}`)
      .then(r => setFinSummary(r.data)).catch(() => {});
  }, [from, to, finGroup, selType]);

  useEffect(() => {
    if (!from || !to) return;
    api.get(`/analytics/driver-trend?from=${from}&to=${to}&group=${trendGroup}${selType ? `&type=${selType}` : ''}`)
      .then(r => setDriverTrend(r.data)).catch(() => {});
  }, [from, to, trendGroup, selType]);

  const cur = trends?.current || {};
  const prev = trends?.previous || {};

  const series = (trends?.series || []).map(s => ({
    date: shortDate(s.date),
    Delivered: Number(s.delivered),
    Rescheduled: Number(s.rescheduled),
    Cancelled: Number(s.cancelled),
    Value: Number(s.value),
  }));

  const active = drivers.filter(d => Number(d.total_stops) > 0);
  const ranked = [...active].sort((a, b) => Number(b.success_rate) - Number(a.success_rate));
  const idle = drivers.length - active.length;

  const finSeries = (finSummary?.series || []).map(s => ({
    date: finGroup === 'day' ? shortDate(s.period) : niceDate(s.period),
    Delivered: Number(s.delivered_value),
    Cancelled: Number(s.cancelled_value),
    Rescheduled: Number(s.rescheduled_value),
  }));

  const DRIVER_COLORS = [C.blue, C.green, C.orange, C.purple, C.red, C.amber];
  const topDriversForTrend = driverTrend
    ? [...driverTrend.drivers]
        .sort((a, b) => {
          const av = Object.values(a.byPeriod).reduce((s, x) => s + x.value, 0);
          const bv = Object.values(b.byPeriod).reduce((s, x) => s + x.value, 0);
          return bv - av;
        })
        .slice(0, 6)
    : [];
  const driverTrendSeries = (driverTrend?.periods || []).map(period => {
    const row = { date: trendGroup === 'week' ? shortDate(period) : niceDate(period) };
    topDriversForTrend.forEach(d => { row[d.name] = d.byPeriod[period]?.success_rate ?? null; });
    return row;
  });

  if (!can('management_report', 'view')) {
    return <div style={{ padding: 24 }}>You don't have access to this page.</div>;
  }

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', marginBottom:18, flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:700, color:'#1e293b', margin:0 }}>Management Report</h1>
          <p style={{ color:'#64748b', fontSize:13, margin:'4px 0 0' }}>
            Driver performance and delivery overview · {trends ? `${niceDate(from)} – ${niceDate(to)}` : '…'}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => setPresetKey(p.key)} style={{
              padding:'8px 16px', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600,
              background: presetKey === p.key ? '#1e293b' : '#fff', color: presetKey === p.key ? '#fff' : '#64748b',
              boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>{p.label}</button>
          ))}
            <div style={{ display:'flex', alignItems:'center', gap:6, background:'#fff', borderRadius:8, padding:'6px 10px', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
              <input type="date" value={customFrom} onChange={e => { setCustomFrom(e.target.value); setPresetKey('custom'); }} style={{ border:'none', fontSize:12, color:'#1e293b', outline:'none', fontFamily:'inherit' }} />
              <span style={{ color:'#94a3b8', fontSize:12 }}>–</span>
              <input type="date" value={customTo} onChange={e => { setCustomTo(e.target.value); setPresetKey('custom'); }} style={{ border:'none', fontSize:12, color:'#1e293b', outline:'none', fontFamily:'inherit' }} />
            </div>
            <select value={selType} onChange={e => setSelType(e.target.value)} style={{
              padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:600,
              background:'#fff', color:'#64748b', boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
              {visibleTypeOptions.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
            </select>
        </div>
      </div>

      {loading && <div style={{ color:'#94a3b8', fontSize:13, marginBottom:12 }}>Loading...</div>}

      {trends && (
        <>
          <div style={{ display:'flex', gap:14, marginBottom:20, flexWrap:'wrap' }}>
            <KpiCard icon={CheckCircle} label="Delivered" value={num(cur.delivered)} color={C.green} current={cur.delivered} previous={prev.delivered} />
            <KpiCard icon={Percent} label="Success rate" value={(cur.success_rate || 0) + '%'} color={C.blue} current={cur.success_rate} previous={prev.success_rate} />
            <KpiCard icon={TrendingUp} label="Value delivered" value={money(cur.value)} color={C.orange} current={cur.value} previous={prev.value} />
            <KpiCard icon={MapPin} label="Distance driven" value={Math.round(cur.km || 0) + ' km'} color={C.purple} current={cur.km} previous={prev.km} />
            <KpiCard icon={XCircle} label="Cancelled" value={num(cur.cancelled)} color={C.red} current={cur.cancelled} previous={prev.cancelled} invert />
          </div>

          <Panel title="Deliveries over time" note="Delivered, rescheduled and cancelled per day">
            {series.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize:12 }} />
                  <Bar dataKey="Delivered" stackId="a" fill={C.green} />
                  <Bar dataKey="Rescheduled" stackId="a" fill={C.purple} />
                  <Bar dataKey="Cancelled" stackId="a" fill={C.red} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No data for this period" />}
          </Panel>

          <Panel title="Value delivered per day" note="AED value of completed deliveries">
            {series.length ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                  <Tooltip formatter={v => money(v)} />
                  <Bar dataKey="Value" fill={C.orange} radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty text="No data for this period" />}
          </Panel>

          <Panel title="Driver scorecard" icon={Users} note={idle ? `Ranked by success rate · ${idle} driver(s) had no deliveries this period` : 'Ranked by success rate'}>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:760 }}>
                <thead><tr style={{ background:'#f8fafc' }}>
                  <th style={th}>#</th><th style={th}>Driver</th><th style={th}>Delivered</th><th style={th}>Success</th>
                  <th style={th}>Stops/day</th><th style={th}>KM</th><th style={th}>Avg travel</th><th style={th}>Avg at shop</th><th style={th}>Value</th>
                </tr></thead>
                <tbody>
                  {ranked.map((d, i) => {
                    const topPerformer = i === 0 && Number(d.success_rate) >= 70;
                    const needsAttention = Number(d.success_rate) < 60;
                    return (
                      <tr key={d.id} style={{ borderTop:'1px solid #f1f5f9', background: topPerformer ? '#f0fdf4' : needsAttention ? '#fef2f2' : 'transparent' }}>
                        <td style={{ ...td, color:'#94a3b8', fontWeight:700 }}>{i + 1}</td>
                        <td style={{ ...td, fontWeight:600, color:'#1e293b' }}>{d.name}</td>
                        <td style={{ ...td, color:C.green, fontWeight:600 }}>{d.delivered}</td>
                        <td style={td}>
                          <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700,
                            background: d.success_rate >= 80 ? '#dcfce7' : d.success_rate >= 60 ? '#fef3c7' : '#fee2e2',
                            color: d.success_rate >= 80 ? C.green : d.success_rate >= 60 ? '#b45309' : C.red }}>
                            {d.success_rate}%</span>
                        </td>
                        <td style={td}>{d.stops_per_day}</td>
                        <td style={td}>{Number(d.km).toFixed(0)}</td>
                        <td style={{ ...td, color:C.blue }}>{mins(d.avg_travel)}</td>
                        <td style={{ ...td, color:C.amber }}>{mins(d.avg_service)}</td>
                        <td style={{ ...td, color:'#1e293b', fontWeight:500 }}>{money(d.value)}</td>
                      </tr>
                    );
                  })}
                  {!ranked.length && <tr><td colSpan={9}><Empty text="No driver activity in this period" /></td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>

          <Panel title="Route / on-time performance" icon={Clock} note="On-time = route finished within its estimated duration">
              {routePerf && routePerf.drivers.length ? (
                <>
                  <div style={{ display:'flex', gap:20, marginBottom:14, flexWrap:'wrap' }}>
                    <div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>Routes completed</div>
                      <div style={{ fontSize:20, fontWeight:700, color:'#1e293b' }}>{num(routePerf.totals.routes)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>On-time rate</div>
                      <div style={{ fontSize:20, fontWeight:700, color: routePerf.totals.on_time_rate >= 80 ? C.green : routePerf.totals.on_time_rate >= 60 ? '#b45309' : C.red }}>
                        {routePerf.totals.on_time_rate == null ? '—' : routePerf.totals.on_time_rate + '%'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize:11, color:'#94a3b8' }}>Late routes</div>
                      <div style={{ fontSize:20, fontWeight:700, color:C.red }}>{num(routePerf.totals.late)}</div>
                    </div>
                  </div>
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
                      <thead><tr style={{ background:'#f8fafc' }}>
                        <th style={th}>Driver</th><th style={th}>Routes</th><th style={th}>On-time</th><th style={th}>Late</th>
                        <th style={th}>On-time %</th><th style={th}>Avg route time</th><th style={th}>Avg / stop</th>
                      </tr></thead>
                      <tbody>
                        {routePerf.drivers.map(d => (
                          <tr key={d.id || d.name} style={{ borderTop:'1px solid #f1f5f9' }}>
                            <td style={{ ...td, fontWeight:600, color:'#1e293b' }}>{d.name || 'Unassigned'}</td>
                            <td style={td}>{d.routes}</td>
                            <td style={{ ...td, color:C.green }}>{d.on_time}</td>
                            <td style={{ ...td, color:C.red }}>{d.late}</td>
                            <td style={td}>
                              {d.on_time_rate == null ? '—' : (
                                <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:700,
                                  background: d.on_time_rate >= 80 ? '#dcfce7' : d.on_time_rate >= 60 ? '#fef3c7' : '#fee2e2',
                                  color: d.on_time_rate >= 80 ? C.green : d.on_time_rate >= 60 ? '#b45309' : C.red }}>
                                  {d.on_time_rate}%</span>
                              )}
                            </td>
                            <td style={td}>{mins(d.avg_duration_min)}</td>
                            <td style={td}>{d.avg_min_per_stop ? d.avg_min_per_stop + ' min' : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : <Empty text="No completed routes in this period" />}
            </Panel>

            <Panel title="Financial summary" icon={TrendingUp} note="Delivered value vs cancelled/rescheduled value, over time">
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                {['day','week','month'].map(g => (
                  <button key={g} onClick={() => setFinGroup(g)} style={{
                    padding:'5px 12px', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600,
                    background: finGroup === g ? '#1e293b' : '#fff', color: finGroup === g ? '#fff' : '#64748b' }}>
                    {g === 'day' ? 'Daily' : g === 'week' ? 'Weekly' : 'Monthly'}
                  </button>
                ))}
              </div>
              {finSummary && (
                <div style={{ display:'flex', gap:20, marginBottom:14, flexWrap:'wrap' }}>
                  <div><div style={{ fontSize:11, color:'#94a3b8' }}>Delivered value</div><div style={{ fontSize:18, fontWeight:700, color:C.green }}>{money(finSummary.totals.delivered_value)}</div></div>
                  <div><div style={{ fontSize:11, color:'#94a3b8' }}>Cancelled value</div><div style={{ fontSize:18, fontWeight:700, color:C.red }}>{money(finSummary.totals.cancelled_value)}</div></div>
                  <div><div style={{ fontSize:11, color:'#94a3b8' }}>Rescheduled value</div><div style={{ fontSize:18, fontWeight:700, color:C.amber }}>{money(finSummary.totals.rescheduled_value)}</div></div>
                </div>
              )}
              {finSeries.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={finSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                    <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} />
                    <Tooltip formatter={v => money(v)} />
                    <Legend wrapperStyle={{ fontSize:12 }} />
                    <Bar dataKey="Delivered" fill={C.green} radius={[4,4,0,0]} />
                    <Bar dataKey="Cancelled" fill={C.red} radius={[4,4,0,0]} />
                    <Bar dataKey="Rescheduled" fill={C.amber} radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Empty text="No data for this period" />}
            </Panel>

            <Panel title="Driver comparison over time" icon={Users} note="Success rate trend — top 6 drivers by value in this range">
              <div style={{ display:'flex', gap:8, marginBottom:12 }}>
                {['week','month'].map(g => (
                  <button key={g} onClick={() => setTrendGroup(g)} style={{
                    padding:'5px 12px', border:'1px solid #e2e8f0', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:600,
                    background: trendGroup === g ? '#1e293b' : '#fff', color: trendGroup === g ? '#fff' : '#64748b' }}>
                    {g === 'week' ? 'Weekly' : 'Monthly'}
                  </button>
                ))}
              </div>
              {driverTrendSeries.length && topDriversForTrend.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={driverTrendSeries}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize:11, fill:'#94a3b8' }} />
                    <YAxis tick={{ fontSize:11, fill:'#94a3b8' }} unit="%" />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize:12 }} />
                    {topDriversForTrend.map((d, i) => (
                      <Line key={d.id} type="monotone" dataKey={d.name} stroke={DRIVER_COLORS[i % DRIVER_COLORS.length]} strokeWidth={2} dot={{ r:3 }} connectNulls />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              ) : <Empty text="No driver activity across this range" />}
            </Panel>

            <Panel title="Customer breakdown" icon={Building2} note="Top 25 customers by value delivered in this period">
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
                  <thead><tr style={{ background:'#f8fafc' }}>
                    <th style={th}>#</th><th style={th}>Customer</th><th style={th}>Code</th>
                    <th style={th}>Total</th><th style={th}>Delivered</th><th style={th}>Cancelled</th><th style={th}>Pending</th><th style={th}>Rescheduled</th><th style={th}>Value</th>
                  </tr></thead>
                  <tbody>
                    {customers.map((c, i) => (
                      <tr key={c.customer_code || c.customer_name || i} style={{ borderTop:'1px solid #f1f5f9' }}>
                        <td style={{ ...td, color:'#94a3b8', fontWeight:700 }}>{i + 1}</td>
                        <td style={{ ...td, fontWeight:600, color:'#1e293b' }}>{c.customer_name || '—'}</td>
                        <td style={{ ...td, color:'#94a3b8' }}>{c.customer_code || '—'}</td>
                        <td style={td}>
                          <Link to={`/tracking?q=${encodeURIComponent(c.customer_code || c.customer_name || '')}&from=${from}&to=${to}`}
                            title="View this customer's invoice history"
                            style={{ color:C.blue, fontWeight:700, textDecoration:'none' }}>
                            {c.total}
                          </Link>
                        </td>
                        <td style={{ ...td, color:C.green }}>{c.delivered}</td>
                        <td style={{ ...td, color:C.red }}>{c.cancelled}</td>
                        <td style={{ ...td, color:C.slate }}>{c.pending}</td>
                        <td style={{ ...td, color:C.amber }}>{c.rescheduled}</td>
                        <td style={{ ...td, color:'#1e293b', fontWeight:500 }}>{money(c.value)}</td>
                      </tr>
                    ))}
                    {!customers.length && <tr><td colSpan={9}><Empty text="No invoices in this period" /></td></tr>}
                  </tbody>
                </table>
              </div>
            </Panel>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16 }}>
            <Panel title="Top cancellation reasons" icon={AlertTriangle}>
              <MiniRank items={problems?.cancel_reasons || []} primary={r => r.reason} valueLabel={r => r.count} />
            </Panel>
            <Panel title="Problem customers" icon={AlertTriangle} note="Most cancelled or rescheduled">
              <MiniRank items={problems?.problem_customers || []}
                primary={c => c.customer_name}
                secondary={c => c.customer_code}
                valueLabel={c => Number(c.cancelled) + Number(c.rescheduled)} />
            </Panel>
            <Panel title="GPS location mismatches" icon={AlertTriangle} note="Avg distance off (m)">
              <MiniRank items={problems?.bad_coordinates || []}
                primary={c => c.customer_name}
                secondary={c => c.customer_code}
                valueLabel={c => Number(c.avg_distance) >= 1000 ? (Number(c.avg_distance)/1000).toFixed(1)+'km' : Math.round(c.avg_distance)+'m'} />
            </Panel>
          </div>
        </>
      )}
    </div>
  );
}
